import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import apiService from '../../services/ApiService';
import { updateMetaTags } from '../../utils/metaTags';
import { useEventPublicPaths, useEventRouteParams } from '../../hooks/useEventRouteParams';
import type { PublicEventData } from '../../types/event';
import PageLoader from '../ui/PageLoader';
import Footer from '../Footer';
import PageBackLink from '../ui/PageBackLink';
import { CalendarIcon } from '@heroicons/react/24/outline';
import CommunityPostFeed from '../community/CommunityPostFeed';
import RegistrationStatusPanel from './RegistrationStatusPanel';
import { PlaceMapCard } from '../places/PlaceMapCard';
import { formatPlaceHeadline } from '../../utils/placeDomain';
import { entryModeDescription, eventJudgingEnabled, eventOffersSpectatorTickets } from '../../utils/registrationStatus';
import EventPublicRoster from './EventPublicRoster';
import EventPublicPassportHunts from './EventPublicPassportHunts';

function formatEventDateTime(startAt: string | null, endAt: string | null): string {
  if (!startAt) return 'Date TBD';
  const start = new Date(startAt.replace(' ', 'T'));
  if (isNaN(start.getTime())) return 'Date TBD';
  const startStr = start.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
  if (!endAt) return startStr;
  const end = new Date(endAt.replace(' ', 'T'));
  if (isNaN(end.getTime())) return startStr;
  const endStr = end.toLocaleString(undefined, { hour: 'numeric', minute: '2-digit' });
  return `${startStr} to ${endStr}`;
}

function formatLocation(event: PublicEventData['event']): string | null {
  const headline = formatPlaceHeadline({
    location_label: event.location,
    formatted_address: event.formatted_address,
    city: event.city,
    state: event.state,
    place_id: event.place_id,
    latitude: event.latitude,
    longitude: event.longitude,
  });
  return headline;
}

const PublicEventPage: React.FC = () => {
  const { apiQuery } = useEventRouteParams();
  const navigate = useNavigate();
  const [data, setData] = useState<PublicEventData | null>(null);
  const paths = useEventPublicPaths(data?.event ?? null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!apiQuery) return;
    const result = await apiService.getPublicEventBySlug(apiQuery);
    setData(result);
  }, [apiQuery]);

  useEffect(() => {
    if (!apiQuery) return;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await apiService.getPublicEventBySlug(apiQuery);
        setData(result);
        const flyer = result.event.flyer_path
          ? apiService.getFileUrl(result.event.flyer_path) || result.event.flyer_path
          : `${window.location.origin}/images/brand/og-default.jpg`;
        updateMetaTags({
          title: `${result.event.title} | Bilt:Spec`,
          description: result.event.description || `${result.event.title} on Bilt:Spec`,
          image: flyer,
          url: window.location.href,
        });
      } catch (err: any) {
        setError(err.message || 'Event not found');
      } finally {
        setLoading(false);
      }
    })();
  }, [apiQuery]);

  if (loading) {
    return (
      <>
        <PageLoader message="Loading event…" />
        <Footer />
      </>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col">
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="text-center max-w-md">
            <p className="kicker mb-3">Event</p>
            <h1 className="heading-section mb-2">Event not found</h1>
            <p className="text-muted mb-6 text-sm leading-relaxed">
              {error || 'This event may be unpublished or no longer available.'}
            </p>
            <Link to="/events" className="btn-primary min-h-[44px] inline-flex">
              Browse events
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const { event, classes, viewer, updates, my_registrations, pricing, feature_flags } = data;
  const location = formatLocation(event);
  const activeRegistrations = my_registrations || [];
  const hasActiveRegistration = activeRegistrations.length > 0;
  const judgingOn = eventJudgingEnabled(event.judging_enabled);
  const canRegister =
    (judgingOn ? classes.length > 0 : true) && !viewer?.is_registered && !hasActiveRegistration;
  const canViewUpdates = Boolean(viewer?.can_view_updates);
  const canPostUpdates = Boolean(viewer?.can_post_updates);
  const ticketsOffered = eventOffersSpectatorTickets(
    event.entry_mode,
    feature_flags?.spectator_tickets_enabled,
  );
  const isCurated = (event.entry_mode || 'open_free').startsWith('pre_approval');
  const showRegisteredState = hasActiveRegistration || Boolean(viewer?.is_registered);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col">
      <div className="flex-1 max-w-2xl mx-auto w-full px-4 sm:px-6 py-8 sm:py-10">
        <PageBackLink label="Back to Events" to="/events" className="mb-4" />
        {event.flyer_path ? (
          <div className="mb-6 -mx-4 sm:mx-0 sm:rounded-2xl overflow-hidden border-0 sm:border border-white/[0.06] bg-[#0a0a0a]">
            <img
              src={apiService.getFileUrl(event.flyer_path) || event.flyer_path}
              alt=""
              className="w-full aspect-[16/10] object-cover"
            />
          </div>
        ) : null}
        <header className="mb-8 pb-6 border-b border-white/[0.06]">
          <p className="kicker mb-3">Event</p>
          <h1 className="heading-display leading-tight">{event.title}</h1>
          {event.organizer_username ? (
            <p className="text-sm text-gray-400 mt-2">
              Organized by <span className="text-gray-300">@{event.organizer_username}</span>
            </p>
          ) : null}
          <div className="mt-5 space-y-2.5 text-muted text-sm sm:text-base">
            <div className="flex items-start gap-3">
              <CalendarIcon className="w-5 h-5 shrink-0 mt-0.5 text-gray-500" aria-hidden />
              <span>{formatEventDateTime(event.start_at, event.end_at)}</span>
            </div>
          </div>
        </header>

        {event.description && (
          <div className="mb-4 text-muted leading-relaxed whitespace-pre-wrap text-sm sm:text-base">
            {event.description}
          </div>
        )}

        {location ? (
          <div className="mb-8">
            <PlaceMapCard
              place={{
                location_label: event.location,
                formatted_address: event.formatted_address,
                place_id: event.place_id,
                latitude: event.latitude,
                longitude: event.longitude,
                city: event.city,
                state: event.state,
              }}
            />
          </div>
        ) : null}

        <p className="text-xs text-gray-500 mb-6">{entryModeDescription(event.entry_mode)}</p>

        {judgingOn ? (
          <section className="mb-8">
            <h2 className="heading-section mb-3">Judged classes</h2>
            {classes.length === 0 ? (
              <p className="text-muted text-sm">Classes will be posted before registration opens.</p>
            ) : (
              <ul className="divide-y divide-white/[0.06] border border-white/[0.06] rounded-xl overflow-hidden">
                {classes.map((cls) => (
                  <li key={cls.id} className="px-4 py-3.5 bg-[#0c0c0c]/40">
                    <p className="font-medium text-white">{cls.name}</p>
                    {cls.description && (
                      <p className="text-sm text-gray-500 mt-0.5">{cls.description}</p>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>
        ) : null}

        <section
          className={
            showRegisteredState
              ? 'card p-5 sm:p-6 mb-8 border border-emerald-800/50 bg-emerald-950/20'
              : 'card p-5 sm:p-6 mb-8'
          }
          aria-labelledby="event-register-heading"
        >
          <h2 id="event-register-heading" className="heading-card mb-2">
            Register your vehicle
          </h2>
          {isCurated && !showRegisteredState ? (
            <p className="text-muted text-sm mb-3 leading-relaxed">
              This is a curated show. Applications are reviewed before confirmation
              {(event.entry_mode || '').includes('paid')
                ? ', and payment is only requested after approval.'
                : '.'}
            </p>
          ) : null}
          {showRegisteredState ? (
            <>
              <p className="text-sm text-emerald-200/90 mb-5 leading-relaxed">
                You are registered. Your build is listed below. Organizer updates for entrants appear further down this page.
              </p>
              {activeRegistrations.map((reg) => (
                <RegistrationStatusPanel
                  key={reg.id}
                  event={event}
                  registration={reg}
                  pricing={pricing}
                  embedded
                  onUpdated={() => void reload()}
                />
              ))}
              <button
                type="button"
                data-testid="event-register-cta"
                disabled
                aria-disabled="true"
                className="btn-primary min-h-[44px] w-full sm:w-auto mt-4 opacity-50 cursor-not-allowed"
              >
                Register your vehicle
              </button>
            </>
          ) : canRegister ? (
            <>
              <p className="text-muted text-sm mb-5 leading-relaxed">
                {judgingOn
                  ? 'Pick a vehicle from your garage. Your build profile travels with you. No duplicate entry.'
                  : 'Pick a vehicle from your garage. This event is not judged, so your build registers as a general entry.'}
              </p>
              <button
                type="button"
                data-testid="event-register-cta"
                onClick={() => navigate(paths.register)}
                className="btn-primary min-h-[44px] w-full sm:w-auto"
              >
                Register your vehicle
              </button>
            </>
          ) : (
            <p className="text-muted text-sm">
              {judgingOn ? 'Check back once classes are posted.' : 'Registration is not available right now.'}
            </p>
          )}
        </section>

        {ticketsOffered ? (
          <section className="card p-5 sm:p-6 mb-8">
            <h2 className="heading-card mb-2">Spectator tickets</h2>
            <p className="text-muted text-sm mb-5 leading-relaxed">
              Purchase admission for spectators. Vehicle entrants register separately.
            </p>
            <Link to={paths.tickets} className="btn-secondary min-h-[44px] inline-flex">
              Buy tickets
            </Link>
          </section>
        ) : null}

        <div className="mb-10">
          <CommunityPostFeed
            scope="event"
            entityId={Number(event.id)}
            canView={canViewUpdates}
            canPost={canPostUpdates}
            initialItems={updates?.items || []}
            initialTotal={Number(updates?.total || 0)}
            lockedHint="Register a vehicle for this event to read organizer updates about schedule, logistics, and day-of notes."
            emptyMemberHint={
              canPostUpdates
                ? 'Post schedule changes and logistics so every registered entrant stays informed.'
                : 'When the organizer posts, you’ll see it here.'
            }
          />
        </div>

        <EventPublicPassportHunts hunts={data.public_passport_hunts} />

        <EventPublicRoster roster={data.public_roster} />

        <div className="text-center pt-4 border-t border-white/[0.06]">
          <Link
            to={paths.archive}
            className="text-sm text-muted hover:text-white min-h-[44px] inline-flex items-center"
          >
            View event archive →
          </Link>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default PublicEventPage;
